import React, { useMemo } from 'react';
import Icon from 'components/AppIcon';
import EmptyState from 'components/EmptyState';

function getDeviationStyle(dev) {
  const abs = Math.abs(dev);
  if (abs > 10) return { row: 'bg-red-50/50 border-l-2 border-red-400', badge: 'bg-red-100 text-red-700', icon: 'AlertCircle', iconColor: '#ef4444' };
  if (abs > 5) return { row: 'bg-amber-50/50 border-l-2 border-amber-400', badge: 'bg-amber-100 text-amber-700', icon: 'AlertTriangle', iconColor: '#d97706' };
  return { row: '', badge: 'bg-emerald-100 text-emerald-700', icon: 'CheckCircle', iconColor: '#059669' };
}

export default function YieldCalibrationPanel({ sales, loading, onFilterByCategory }) {
  const calibrationData = useMemo(() => {
    const groups = {};
    (sales || [])?.forEach(s => {
      if (!s?.yield_est || !s?.actual_yield_pct) return;
      const key = `${s?.metal_name}||${s?.category_name}`;
      if (!groups?.[key]) groups[key] = { metal: s?.metal_name, category: s?.category_name, estValues: [], realValues: [] };
      groups?.[key]?.estValues?.push(parseFloat(s?.yield_est));
      groups?.[key]?.realValues?.push(parseFloat(s?.actual_yield_pct));
    });
    return Object.values(groups)
      ?.map(g => {
        const avgEst = g?.estValues?.reduce((s, v) => s + v, 0) / g?.estValues?.length;
        const avgReal = g?.realValues?.reduce((s, v) => s + v, 0) / g?.realValues?.length;
        const deviation = avgReal - avgEst;
        const count = g?.estValues?.length;
        return { metal: g?.metal, category: g?.category, avgEst, avgReal, deviation, count };
      })
      ?.sort((a, b) => Math.abs(b?.deviation) - Math.abs(a?.deviation));
  }, [sales]);

  const alertCount = calibrationData?.filter(r => Math.abs(r?.deviation) > 5)?.length;

  return (
    <div className="bg-card border border-border rounded-xl" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Target" size={15} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Calibración de Yield</h2>
          {alertCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {alertCount} desviaciones
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> &gt;5%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> &gt;10%</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-muted-foreground font-medium">Metal</th>
              <th className="px-4 py-2.5 text-left text-muted-foreground font-medium">Categoría</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Yield Est. Medio</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Yield Real Medio</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Desviación</th>
              <th className="px-4 py-2.5 text-center text-muted-foreground font-medium">Tendencia</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Muestras</th>
              {onFilterByCategory && (
                <th className="px-4 py-2.5 text-center text-muted-foreground font-medium">Ventas</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 })?.map((_, i) => (
                <tr key={i} className="border-b border-border/50">
                  {Array.from({ length: onFilterByCategory ? 8 : 7 })?.map((_, j) => (
                    <td key={j} className="px-4 py-2.5"><div className="h-3.5 bg-muted rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : calibrationData?.length === 0 ? (
              <EmptyState
                icon="Target"
                title="Sin datos de calibración suficientes"
                description="Se necesitan al menos 3 ventas por categoría para calcular la desviación."
                colSpan={onFilterByCategory ? 8 : 7}
              />
            ) : (
              calibrationData?.map((row, idx) => {
                const style = getDeviationStyle(row?.deviation);
                return (
                  <tr key={idx} className={`border-b border-border/50 transition-colors ${style?.row}`}>
                    <td className="px-4 py-2.5 font-medium text-foreground">{row?.metal}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{row?.category}</td>
                    <td className="px-4 py-2.5 font-mono text-right">{row?.avgEst?.toFixed(1)}%</td>
                    <td className="px-4 py-2.5 font-mono text-right">{row?.avgReal?.toFixed(1)}%</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono font-semibold ${style?.badge}`}>
                        <Icon name={style?.icon} size={10} color={style?.iconColor} />
                        {row?.deviation > 0 ? '+' : ''}{row?.deviation?.toFixed(1)}pp
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Icon
                        name={row?.deviation > 0 ? 'TrendingUp' : row?.deviation < 0 ? 'TrendingDown' : 'Minus'}
                        size={14}
                        color={row?.deviation > 0 ? '#059669' : row?.deviation < 0 ? '#ef4444' : '#94a3b8'}
                        strokeWidth={2}
                      />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-right text-muted-foreground">{row?.count}</td>
                    {onFilterByCategory && (
                      <td className="px-4 py-2.5 text-center">
                        <button
                          onClick={() => onFilterByCategory(row?.category)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors text-foreground"
                          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                          title={`Ver ventas de ${row?.category}`}
                        >
                          <Icon name="ExternalLink" size={10} color="var(--color-accent)" />
                          Ver ventas
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
